import type {
  FeedbackInboxCounts,
  FeedbackInboxCountsQueryParams,
  FeedbackInboxCountsWire,
  FeedbackInboxDetail,
  FeedbackInboxDetailWire,
  FeedbackInboxItem,
  FeedbackInboxItemWire,
  FeedbackInboxPage,
  FeedbackInboxPageWire,
  FeedbackInboxQueryParams,
  FeedbackInboxResolutionEvent,
  FeedbackInboxResolutionEventWire,
  InboxLocation,
  InboxLocationWire,
  ResolutionType,
  ResolveFeedbackInboxItemBody,
  ResolveFeedbackInboxItemParams,
} from './feedbackInbox.types'
import { api } from './index'

// ---------------------------------------------------------------------------
// Wire → view normalizers. Exported for dedicated transform unit tests — the
// slice's transformResponse delegates to these (mirrors
// transformOrderRating / transformConversation elsewhere in src/api).
// ---------------------------------------------------------------------------

// nexus sends `""` for a guest with a blank first name; the components'
// `?? t('unknown_guest')` fallbacks only fire on null. Real names are
// returned untouched — `trim()` only decides blankness.
const blankToNull = (value: string | null | undefined): string | null =>
  value == null || value.trim() === '' ? null : value

export const normalizeFeedbackInboxItem = (
  wire: FeedbackInboxItemWire
): FeedbackInboxItem => ({
  id: wire.id,
  kind: wire.kind,
  status: wire.status,
  rating: wire.rating,
  preview: wire.preview,
  date: wire.date,
  orderNumber: wire.order_number,
  conversationId: wire.conversation_id,
  conversationStatus: wire.conversation_status,
  hasEscalation: wire.has_escalation,
  resolutionType: wire.resolution_type,
  customer: {
    name: blankToNull(wire.customer.name),
    userId: wire.customer.user_id,
    uid: wire.customer.uid,
  },
  location: wire.location
    ? { id: wire.location.id, name: wire.location.name }
    : null,
  resolvedBy: wire.resolved_by
    ? {
        kind: wire.resolved_by.kind,
        name: wire.resolved_by.name,
        at: wire.resolved_by.at,
      }
    : null,
})

export const normalizeFeedbackInboxMeta = (
  wire: FeedbackInboxPageWire['meta']
): FeedbackInboxPage['meta'] => ({
  page: wire.page,
  perPage: wire.per_page,
  totalCount: wire.total_count,
  totalPages: wire.total_pages,
})

export const normalizeFeedbackInboxCounts = (
  wire: FeedbackInboxCountsWire
): FeedbackInboxCounts => ({
  feedback: wire.feedback,
  recoveryAi: wire.recovery_ai,
  badge: wire.badge,
})

const normalizeResolutionEvent = (
  wire: FeedbackInboxResolutionEventWire
): FeedbackInboxResolutionEvent => ({
  action: wire.action,
  by: { kind: wire.by.kind, name: wire.by.name },
  at: wire.at,
  resolutionType: wire.resolution_type,
})

// Pre-`resolution_history` nexus: rebuild what the singular fields can say
// (at most the latest reopen + the standing resolve). Delete with the
// `reopened` wire field once nexus ships history. Also seeds the msw mock.
export const legacyResolutionHistory = (
  wire: FeedbackInboxDetailWire
): FeedbackInboxResolutionEventWire[] => {
  const entries: FeedbackInboxResolutionEventWire[] = []
  // Both set means reopened first (a reopen clears the resolution), so on a
  // same-second tie the reopen goes first: push it first, tie-break by index.
  if (wire.reopened) {
    entries.push({
      action: 'reopened',
      by: { kind: wire.reopened.by.kind, name: wire.reopened.by.name },
      at: wire.reopened.at,
      resolution_type: null,
    })
  }
  if (wire.resolved_by) {
    entries.push({
      action: 'resolved',
      by: { kind: wire.resolved_by.kind, name: wire.resolved_by.name },
      at: wire.resolved_by.at,
      resolution_type: wire.resolution_type,
    })
  }
  return entries
    .map((entry, index) => ({ entry, index }))
    .sort(
      (a, b) =>
        Date.parse(a.entry.at) - Date.parse(b.entry.at) || a.index - b.index
    )
    .map(({ entry }) => entry)
}

export const normalizeFeedbackInboxDetail = (
  wire: FeedbackInboxDetailWire
): FeedbackInboxDetail => ({
  // Every FeedbackInboxItem field EXCEPT `customer` (widened below to the
  // richer detail shape) — can't delegate to normalizeFeedbackInboxItem
  // here, since its `customer` param type is incompatible with the detail
  // wire's `customer`.
  id: wire.id,
  kind: wire.kind,
  status: wire.status,
  rating: wire.rating,
  preview: wire.preview,
  date: wire.date,
  orderNumber: wire.order_number,
  conversationId: wire.conversation_id,
  conversationStatus: wire.conversation_status,
  resolutionType: wire.resolution_type,
  location: wire.location
    ? { id: wire.location.id, name: wire.location.name }
    : null,
  resolvedBy: wire.resolved_by
    ? {
        kind: wire.resolved_by.kind,
        name: wire.resolved_by.name,
        at: wire.resolved_by.at,
      }
    : null,
  channel: wire.channel,
  messages: wire.messages,
  operatorEmails: wire.operator_emails.map(email => ({
    authorName: email.author_name,
    subject: email.subject,
    body: email.body,
    sentAt: email.sent_at,
  })),
  escalation: wire.escalation
    ? { reason: wire.escalation.reason, at: wire.escalation.at }
    : null,
  reopened: wire.reopened
    ? {
        by: { kind: wire.reopened.by.kind, name: wire.reopened.by.name },
        at: wire.reopened.at,
      }
    : null,
  resolutionHistory: (
    wire.resolution_history ?? legacyResolutionHistory(wire)
  ).map(normalizeResolutionEvent),
  restrictedEvent: wire.restricted_event
    ? {
        by: {
          kind: wire.restricted_event.by.kind,
          name: wire.restricted_event.by.name,
        },
        at: wire.restricted_event.at,
      }
    : null,
  customer: {
    name: blankToNull(wire.customer.name),
    firstName: blankToNull(wire.customer.first_name),
    lastName: blankToNull(wire.customer.last_name),
    email: wire.customer.email,
    // S4 (AI-1353): pre-BE fallback is the old rule (a null email blocks).
    emailState:
      wire.customer.email_state ??
      (wire.customer.email !== null ? 'known' : 'absent'),
    phone: wire.customer.phone,
    uid: wire.customer.uid,
    loyaltyTier: {
      token: wire.customer.loyalty_tier?.token ?? null,
      name: wire.customer.loyalty_tier?.name ?? null,
    },
    totalVisits: wire.customer.total_visits,
    lifetimeSpendCents: wire.customer.lifetime_spend_cents,
    priorResolutionCount: wire.customer.prior_resolution_count,
    restricted: wire.customer.restricted,
    // TODO(Q15): see FeedbackInboxDetailCustomerWire's doc comment.
    // `null` when the wire omits the field, matching every sibling in this
    // normalizer. These two were `undefined` so that `toEqual` assertions
    // predating the fields kept passing (Jest ignores `undefined`-valued
    // keys but not explicit `null` ones) — the assertions were updated
    // instead, rather than shaping the contract around them.
    thumbsDown90d: wire.customer.thumbs_down_90d
      ? {
          count: wire.customer.thumbs_down_90d.count,
          rewardedCents: wire.customer.thumbs_down_90d.rewarded_cents,
        }
      : null,
    returnedAfter: wire.customer.returned_after
      ? {
          returned: wire.customer.returned_after.returned,
          total: wire.customer.returned_after.total,
        }
      : null,
  },
  order: wire.order
    ? {
        orderId: wire.order.order_id,
        orderNumber: wire.order.order_number,
        locationName: wire.order.location_name,
        orderDate: wire.order.order_date,
        orderTotalCents: wire.order.order_total_cents,
        subtotalCents: wire.order.subtotal_cents ?? null,
        taxCents: wire.order.tax_cents,
        promo: wire.order.promo,
        discounts: (wire.order.discounts ?? []).map(discount => ({
          label: discount.label,
          amountCents: discount.amount_cents,
        })),
        items: (wire.order.items ?? []).map(item => ({
          name: item.name,
          quantity: item.qty,
          priceCents: item.price_cents ?? undefined,
          modifier: item.modifier ?? null,
        })),
        deliveryMethod: wire.order.delivery_method,
        customerFirstName: wire.order.customer_first_name ?? null,
      }
    : null,
  recentActivities: wire.recent_activities.map(activity => ({
    id: activity.id,
    rating: activity.rating,
    date: activity.date,
    comment: activity.comment,
    resolvedBy: activity.resolved_by
      ? {
          kind: activity.resolved_by.kind,
          name: activity.resolved_by.name,
          at: activity.resolved_by.at,
        }
      : null,
  })),
  otherOpenEscalationIds: wire.other_open_escalation_ids ?? [],
})

export const normalizeInboxLocation = (
  wire: InboxLocationWire
): InboxLocation => ({
  id: wire.id,
  // Callers run string methods on the name; the UI labels a blank one.
  name: wire.name ?? '',
  // S1 (Q-S1-2): `?? null`, not `?? false` — a missing wire field means
  // "unknown" (pre-S5), not "RAI is off" for this location.
  recoveryAiEnabled: wire.recovery_ai_enabled ?? null,
})

// ---------------------------------------------------------------------------
// Query-string builders. Grape (nexus) expects repeated keys for arrays —
// `location_ids[]=1&location_ids[]=2` — matching the existing
// `fields[]=`/`where[][…]=` precedent in api/pickerData.ts. RTK Query's
// fetchBaseQuery only comma-joins arrays with its default `params`
// serializer (`new URLSearchParams({a:[1,2]})` → `a=1%2C2`), so we build the
// URLSearchParams ourselves and hand it to `params` directly — fetchBaseQuery
// passes a URLSearchParams instance through `new URLSearchParams(params)`
// unchanged, which preserves repeated keys. Exported for their unit tests.
// ---------------------------------------------------------------------------

export const buildFeedbackInboxSearchParams = (
  params: FeedbackInboxQueryParams
): URLSearchParams => {
  const search = new URLSearchParams()
  search.set('kind', params.kind)
  search.set('status', params.status)
  if (params.rating) search.set('rating', params.rating)
  ;(params.location_ids ?? []).forEach(id =>
    search.append('location_ids[]', String(id))
  )
  if (params.search) search.set('search', params.search)
  search.set('page', String(params.page))
  search.set('per_page', String(params.per_page))
  return search
}

export const buildFeedbackInboxCountsSearchParams = (
  params: FeedbackInboxCountsQueryParams
): URLSearchParams => {
  const search = new URLSearchParams()
  if (params.status) search.set('status', params.status)
  ;(params.location_ids ?? []).forEach(id =>
    search.append('location_ids[]', String(id))
  )
  if (params.rating) search.set('rating', params.rating)
  return search
}

// Mirrors the nexus derivation described in docs/03-api-contract.md
// ("resolution_type" row): the FE never sends resolved_with_message /
// resolved_with_reward directly — the header's explicit
// resolved_without_reward passes through, and everything else is derived
// from which fields the composer actually populated. Exported for its unit
// test; used only to drive the OPTIMISTIC patch below — the real value
// always comes from the refetch that invalidatesTags triggers.
export const deriveResolutionType = (
  body: ResolveFeedbackInboxItemBody
): ResolutionType => {
  if (body.resolution_type === 'resolved_without_reward') {
    return 'resolved_without_reward'
  }
  if ((body.reward_ids && body.reward_ids.length > 0) || body.points) {
    return 'resolved_with_reward'
  }
  if (body.reply_message) {
    return 'resolved_with_message'
  }
  return 'resolved_without_reward'
}

// ---------------------------------------------------------------------------
// Cached `getFeedbackInbox` args lookup. RTK Query 1.9 has no
// `selectCachedArgsForQuery` (added later), so there is no built-in selector
// to enumerate every cached list query to patch it optimistically on
// resolve/unresolve. Rather than tracking args in a module-level singleton
// (leaks across stores/tests, and `JSON.stringify` keying is key-order-
// sensitive), this reads the live cache straight out of the store passed to
// `onQueryStarted` — `state[api.reducerPath].queries` already holds every
// active query's `originalArgs`, keyed by requestId.
//
// `getState()` isn't wired to an app-wide `RootState` type in this repo (see
// `(getState() as any).auth.token` in src/api/index.ts) — this is the single
// place that widens it, scoped to exactly the state slice this needs.
// ---------------------------------------------------------------------------

const selectCachedListArgs = (state: unknown): FeedbackInboxQueryParams[] => {
  const apiState = (
    state as Record<
      typeof api.reducerPath,
      ReturnType<typeof feedbackInboxApi.reducer>
    >
  )[api.reducerPath]

  return Object.values(apiState.queries)
    .filter(
      (entry): entry is NonNullable<typeof entry> =>
        !!entry &&
        entry.endpointName === 'getFeedbackInbox' &&
        entry.originalArgs !== undefined
    )
    .map(entry => entry.originalArgs as FeedbackInboxQueryParams)
}

// A conversation-backed resolve/unresolve runs the same
// Recovery::Conversations interactor recoveryAi.ts's resolveCase already
// invalidates RecoveryActivities/RecoveryMetrics/RecoveryConversation for
// (PR #4174 review, Darshan) — read the cached detail rather than widening
// the mutation's own arg shape back out to carry conversationId.
const selectCachedConversationId = (
  state: unknown,
  id: string
): string | null => {
  const apiState = (
    state as Record<
      typeof api.reducerPath,
      ReturnType<typeof feedbackInboxApi.reducer>
    >
  )[api.reducerPath]

  const entry = Object.values(apiState.queries).find(
    (e): e is NonNullable<typeof e> =>
      !!e && e.endpointName === 'getFeedbackInboxItem' && e.originalArgs === id
  )

  return (
    (entry?.data as FeedbackInboxDetail | undefined)?.conversationId ?? null
  )
}

export const feedbackInboxApi = api.injectEndpoints({
  endpoints: builder => ({
    getFeedbackInbox: builder.query<
      FeedbackInboxPage,
      FeedbackInboxQueryParams
    >({
      query: params => ({
        url: 'feedback_inbox',
        params: buildFeedbackInboxSearchParams(params),
      }),
      transformResponse: (
        response: FeedbackInboxPageWire
      ): FeedbackInboxPage => ({
        items: response.items.map(normalizeFeedbackInboxItem),
        meta: normalizeFeedbackInboxMeta(response.meta),
      }),
      providesTags: ['FeedbackInbox'],
    }),

    getFeedbackInboxCounts: builder.query<
      FeedbackInboxCounts,
      FeedbackInboxCountsQueryParams
    >({
      query: params => ({
        url: 'feedback_inbox/counts',
        params: buildFeedbackInboxCountsSearchParams(params),
      }),
      transformResponse: normalizeFeedbackInboxCounts,
      providesTags: ['FeedbackInboxCounts'],
    }),

    getFeedbackInboxItem: builder.query<FeedbackInboxDetail, string>({
      query: id => ({ url: `feedback_inbox/${id}` }),
      transformResponse: normalizeFeedbackInboxDetail,
      providesTags: (_result, _error, id) => [
        { type: 'FeedbackInboxItem', id },
      ],
    }),

    getInboxLocations: builder.query<InboxLocation[], void>({
      query: () => ({ url: 'locations' }),
      transformResponse: (response: { locations: InboxLocationWire[] }) =>
        response.locations.map(normalizeInboxLocation),
    }),

    // One ingress (AI-1349, Darshan): nexus routes by kind server-side, so the
    // FE sends only the rating hashid + body. The legacy per-kind endpoints
    // stay for CaseDetail / RatingDetail.
    resolveFeedbackInboxItem: builder.mutation<
      void,
      ResolveFeedbackInboxItemParams
    >({
      query: ({ id, body }) => ({
        url: `feedback_inbox/${id}/resolve`,
        method: 'POST',
        body,
      }),
      // The inbox shows its own inline permission banner for a 403 (AI-1350),
      // so the global RBAC toast would say the same thing twice.
      extraOptions: { skipErrorToast: true },
      // Optimistic patch: flip status/resolvedBy/resolutionType on both the
      // item's own detail cache entry and every currently-cached list page it
      // appears on, so the UI (auto-advance, resolve animation, keep-
      // selection) can react before the round trip completes. Rolled back on
      // failure — and on failure ONLY the item's own detail tag is
      // invalidated (mirrors orderRatings.ts's resolveOrderRating: a genuine
      // failure must not drop the flat list/counts tags, which would refetch
      // and potentially hide a still-unresolved row under "Open"). The
      // success path invalidates all three tags so the authoritative
      // resolvedBy.name (which this layer cannot know ahead of the response)
      // lands via refetch.
      onQueryStarted: async (
        { id, body },
        { dispatch, getState, queryFulfilled }
      ) => {
        const resolvedAt = new Date().toISOString()
        const resolutionType = deriveResolutionType(body)
        const conversationId = selectCachedConversationId(getState(), id)
        const patches = [
          dispatch(
            feedbackInboxApi.util.updateQueryData(
              'getFeedbackInboxItem',
              id,
              draft => {
                draft.status = 'resolved'
                draft.resolutionType = resolutionType
                draft.resolvedBy = {
                  kind: 'operator',
                  name: null,
                  at: resolvedAt,
                }
                draft.resolutionHistory.push({
                  action: 'resolved',
                  by: { kind: 'operator', name: null },
                  at: resolvedAt,
                  resolutionType,
                })
                // Mirrors the refetch: a conversation-backed resolve resolves
                // the conversation.
                if (draft.conversationId !== null) {
                  draft.conversationStatus = 'resolved'
                }
              }
            )
          ),
          ...selectCachedListArgs(getState()).map(args =>
            dispatch(
              feedbackInboxApi.util.updateQueryData(
                'getFeedbackInbox',
                args,
                draft => {
                  const item = draft.items.find(i => i.id === id)
                  if (!item) return
                  item.status = 'resolved'
                  item.resolutionType = resolutionType
                  item.resolvedBy = {
                    kind: 'operator',
                    name: null,
                    at: resolvedAt,
                  }
                  if (item.conversationId !== null) {
                    item.conversationStatus = 'resolved'
                  }
                }
              )
            )
          ),
        ]
        try {
          await queryFulfilled
          // A conversation-backed resolve runs Recovery::Conversations::Resolve
          // server-side — refresh the Recovery AI dashboard the same way
          // resolveCase (recoveryAi.ts) does for the same interactor.
          if (conversationId !== null) {
            dispatch(
              feedbackInboxApi.util.invalidateTags([
                'RecoveryActivities',
                'RecoveryMetrics',
                { type: 'RecoveryConversation', id: conversationId },
              ])
            )
          }
        } catch {
          patches.forEach(patch => patch.undo())
          dispatch(
            feedbackInboxApi.util.invalidateTags([
              { type: 'FeedbackInboxItem', id },
            ])
          )
        }
      },
      invalidatesTags: (_result, error, { id }) =>
        error
          ? []
          : [
              { type: 'FeedbackInboxItem', id },
              'FeedbackInbox',
              'FeedbackInboxCounts',
            ],
    }),

    // Single endpoint regardless of kind — the backend dispatches by kind
    // server-side (docs/03-api-contract.md, unresolve row).
    unresolveFeedbackInboxItem: builder.mutation<void, string>({
      query: id => ({
        url: `feedback_inbox/${id}/unresolve`,
        method: 'POST',
      }),
      // The inbox shows its own inline permission banner for a 403 (AI-1350),
      // so the global RBAC toast would say the same thing twice.
      extraOptions: { skipErrorToast: true },
      onQueryStarted: async (id, { dispatch, getState, queryFulfilled }) => {
        const reopenedAt = new Date().toISOString()
        const conversationId = selectCachedConversationId(getState(), id)
        const patches = [
          dispatch(
            feedbackInboxApi.util.updateQueryData(
              'getFeedbackInboxItem',
              id,
              draft => {
                draft.status = 'open'
                draft.resolutionType = null
                draft.resolvedBy = null
                // Append only: the undone resolve stays in the history.
                draft.reopened = {
                  by: { kind: 'operator', name: null },
                  at: reopenedAt,
                }
                draft.resolutionHistory.push({
                  action: 'reopened',
                  by: { kind: 'operator', name: null },
                  at: reopenedAt,
                  resolutionType: null,
                })
                // C2: a reopen always lands a conversation on `escalated`; any
                // escalation marker the row carries is left alone, so the
                // optimistic label is the refetched one.
                if (draft.conversationId !== null) {
                  draft.conversationStatus = 'escalated'
                }
              }
            )
          ),
          ...selectCachedListArgs(getState()).map(args =>
            dispatch(
              feedbackInboxApi.util.updateQueryData(
                'getFeedbackInbox',
                args,
                draft => {
                  const item = draft.items.find(i => i.id === id)
                  if (!item) return
                  item.status = 'open'
                  item.resolutionType = null
                  item.resolvedBy = null
                  if (item.conversationId !== null) {
                    item.conversationStatus = 'escalated'
                  }
                }
              )
            )
          ),
        ]
        try {
          await queryFulfilled
          // A reopen runs the same conversation interactor family a resolve
          // does — see the matching comment in resolveFeedbackInboxItem above.
          if (conversationId !== null) {
            dispatch(
              feedbackInboxApi.util.invalidateTags([
                'RecoveryActivities',
                'RecoveryMetrics',
                { type: 'RecoveryConversation', id: conversationId },
              ])
            )
          }
        } catch {
          patches.forEach(patch => patch.undo())
          dispatch(
            feedbackInboxApi.util.invalidateTags([
              { type: 'FeedbackInboxItem', id },
            ])
          )
        }
      },
      invalidatesTags: (_result, error, id) =>
        error
          ? []
          : [
              { type: 'FeedbackInboxItem', id },
              'FeedbackInbox',
              'FeedbackInboxCounts',
            ],
    }),
  }),
})

export const {
  useGetFeedbackInboxQuery,
  useGetFeedbackInboxCountsQuery,
  useGetFeedbackInboxItemQuery,
  useGetInboxLocationsQuery,
  useResolveFeedbackInboxItemMutation,
  useUnresolveFeedbackInboxItemMutation,
} = feedbackInboxApi
